/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import isNil from 'lodash/isNil';
import {
    SecurityHelper,
    SpinnerModal,
    Stack,
    Box,
    Icon,
    Heading,
    TextBlock,
    RecaptchaEnterprise,
    HtmlParser,
} from 'sarsaparilla';
import axios from 'axios';
import { EnterpriseRecaptchaModal } from 'shared-ui/components/RecaptchaModal/EnterpriseRecaptchaModal';

import { getCreateReservationUrl } from '../../api/endpoints';
import { convertServerErrorToDisplayError } from '../../utils/convertServerErrorToDisplayError';

export const CHALLENGE_V2_REGION = 'WEST';
export const CHALLENGE_ENTERPRISE_REGION = 'ENTERPRISE';

export const ERR_V2_REQUIRED = 'additional challenge required';

const V2_CAPTCHA = 'V2CAPTCHA';
const V3_CAPTCHA = 'V3CAPTCHA';
const RECAPTCHA_ACTION = 'timedEntryMobileCreateReservationPage';

const SPINNER_MESSAGES = [
    'We’re experiencing heavy traffic, but we’re still trying to secure your spot.',
    'To increase your chance of getting through, do not refresh or attempt multiple bookings from different browsers or tabs.',
];

export function MobileCreateReservationContainer() {
    const [bookingError, setBookingError] = useState();
    const [isRecaptchaModalOpen, setRecaptchaModalOpen] = useState(false);
    const [isSpinnerModalOpen, setSpinnerModalOpen] = useState(true);
    const [isFetching, setFetching] = useState(true);
    const spinnerModalSize = isFetching ? 'sm' : 'md';
    const recaptchaRef = useRef(null);
    const dispatch = useDispatch();

    const [searchParams] = useSearchParams();
    const facilityId = searchParams.get('facility_id');
    const tourId = searchParams.get('tour_id');
    const tourDate = searchParams.get('tour_date');
    const tourTime = searchParams.get('tour_time');
    const tickets = searchParams.get('tickets');

    const v2Token = useSelector((state) => state.recaptcha.verification);

    const makeBookingApiRequest = async (system) => {
        const ticket_count_by_guest_type_id = {};
        const ticketsArray = tickets.split(',');
        ticketsArray.forEach((ticketType) => {
            const name = ticketType.split(':')[0];
            const quantity = parseInt(ticketType.split(':')[1], 10);
            ticket_count_by_guest_type_id[name] = quantity;
        });

        const reservationRequest = {
            reservation: {
                facility_id: facilityId,
                tour_id: tourId,
                status: 'DRAFT',
                tour_date: tourDate,
                tour_time: tourTime,
                ticket_count_by_guest_type_id,
                passes_by_guest_type_id: {},
            },
            reservationoptions: {
                targetstatus: 'HOLD',
            },
        };

        if (system) {
            reservationRequest.system = system;
        }

        // Do the thing where we send the reservation to the api
        // No try catch because a thrown error will be caught by attemptBookingWithRetry
        // so we can see if we need to retry

        const { data } = await axios.post(getCreateReservationUrl(), reservationRequest, {
            headers: {
                Authorization: SecurityHelper.getAuthHeader(),
            },
        });

        const reservationId = data?.reservation_id;

        if (!reservationId) {
            throw new Error('Reservation did not succeed');
        } else {
            return reservationId;
        }
    };

    const processV3 = () => {
        return new Promise((resolve, reject) => {
            // wait a bit before trying to fetch recaptcha, it doesn't always work immediately after loading page
            // this should not be used for mobile apps, it won't produce good confidence scores or may not work at all
            setTimeout(() => {
                recaptchaRef?.current
                    ?.executeAsync()
                    .then((token) => {
                        const recaptcha = {
                            code: token,
                            section: RECAPTCHA_ACTION,
                            region: CHALLENGE_ENTERPRISE_REGION,
                        };
                        makeBookingApiRequest(recaptcha)
                            .then((response) => resolve(response))
                            .catch((error) => reject(error));
                    })
                    .catch(() => {
                        makeBookingApiRequest(null)
                            .then((response) => resolve(response))
                            .catch((error) => reject(error));
                    });
            }, 2000);
        });
    };

    const processV2 = () => {
        const recaptcha = {
            section: RECAPTCHA_ACTION,
            code: v2Token,
            region: CHALLENGE_V2_REGION,
        };

        return makeBookingApiRequest(recaptcha);
    };

    const attemptBookingWithRetry = async ({ type, recaptcha }) => {
        setFetching(true);
        setSpinnerModalOpen(true);
        setRecaptchaModalOpen(false);

        let processFunc;

        switch (type) {
            case V3_CAPTCHA:
                processFunc = processV3;
                break;
            case V2_CAPTCHA:
                processFunc = processV2;
                break;
            default:
                if (recaptcha) {
                    processFunc = () => {
                        return makeBookingApiRequest(recaptcha);
                    };
                } else {
                    processFunc = makeBookingApiRequest;
                }
        }

        processFunc()
            .then((reservationId) => {
                setFetching(false);
                setSpinnerModalOpen(false);
                window.location.href = `/timed-entry/reservation/${reservationId}`;
            })
            .catch((error) => {
                const errorMsg = error?.response?.data?.error || error.message;

                const newErrorMsg = convertServerErrorToDisplayError(errorMsg);
                setBookingError(newErrorMsg);

                if (errorMsg === ERR_V2_REQUIRED) {
                    setSpinnerModalOpen(false);
                    setRecaptchaModalOpen(true);
                } else {
                    setFetching(false);
                }
            });
    };

    useEffect(() => {
        setBookingError(null);

        if (SecurityHelper.isGuest() || SecurityHelper.isLoggedIn()) {
            let type = null;
            let recaptcha = null;
            // only use mobile recaptcha if running in mobile app
            if (
                SecurityHelper.getMobileNativeRecaptchaToken() &&
                SecurityHelper.getMobileNativeRecaptchaType()
            ) {
                recaptcha = {
                    section: RECAPTCHA_ACTION,
                    code: SecurityHelper.getMobileNativeRecaptchaToken(),
                    region: SecurityHelper.getMobileNativeRecaptchaType(),
                };
            } else {
                type = V3_CAPTCHA;
            }

            attemptBookingWithRetry({ type, recaptcha });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, facilityId]);

    const handleSpinnerModalClose = () => {
        setSpinnerModalOpen(false);
        // Redirect to this route to fix these issues R1S-38582 - R1S-38581 (Mobile app should detect this url change in order to navigate to the right screen inside the Mobile App)
        window.location.href = `/timed-entry/${facilityId}/ticket/${tourId}`;
    };

    return (
        <>
            <RecaptchaEnterprise
                action={RECAPTCHA_ACTION}
                ref={recaptchaRef}
                size="invisible"
                sitekey={process.env.RECAPTCHA_ENTERPRISE_KEY_INVIS}
            />
            <SpinnerModal
                isOpen={isSpinnerModalOpen}
                isSpinning={isFetching}
                messages={SPINNER_MESSAGES}
                shouldShowCloseButton={!isFetching}
                size={spinnerModalSize}
                onRequestClose={handleSpinnerModalClose}
            >
                <Stack className="mt-6">
                    <Box alignX="center" style={{ color: '#d14529' }}>
                        <Icon iconName="warning" size="xl" />
                    </Box>
                    <Heading
                        headingLevel={1}
                        appearance="h4"
                        style={{ textAlign: 'center' }}
                    >
                        Sorry, we couldn’t make this reservation
                    </Heading>
                    <TextBlock className="mt-3" textAlign="center">
                        <div>
                            <HtmlParser html={bookingError} />
                        </div>
                    </TextBlock>
                </Stack>
            </SpinnerModal>
            <EnterpriseRecaptchaModal
                isOpen={isRecaptchaModalOpen}
                closeModal={() => {
                    attemptBookingWithRetry({ type: V2_CAPTCHA });
                }}
                solved={v2Token !== '' && !isNil(v2Token)}
            />
        </>
    );
}

// cSpell:ignore recaptchav, nocaptcha, targetstatus, reservationoptions, RECAPTCHA_ENTERPRISE_KEY_INVIS
